import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { App, Environment, Platform, Project } from '@hangar/shared';
import { ApiError } from './client';
import { createApp, createEnvironment, createProject, getMe, listApps, listProfiles, listProjects, resolveAppEnv, updateApp, updateEnvironment, updateProject, type AppCreateInput, type AppPatchInput, type EnvironmentPatchInput, type EnvironmentWriteInput, type ProjectCreateInput, type ProjectPatchInput } from './endpoints';
import { qk } from './queryKeys';

export const useMe = () => useQuery({ queryKey: qk.me, queryFn: getMe, staleTime: 5 * 60_000 });

export const useProjects = () =>
  useQuery({ queryKey: qk.projects, queryFn: async () => (await listProjects()).data.filter((p) => !p.archivedAt) });

export const useApps = (projectId: string | undefined) =>
  useQuery({
    queryKey: qk.apps(projectId ?? ''),
    queryFn: async () => (await listApps(projectId ?? '')).data.filter((a) => !a.archivedAt),
    enabled: Boolean(projectId),
  });

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: ProjectCreateInput; idempotencyKey: string }) => createProject(v.body, v.idempotencyKey),
    onSuccess: (project) => {
      qc.setQueryData<Project[]>(qk.projects, (old) => (old ? [...old.filter((p) => p.id !== project.id), project] : [project]));
      void qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
}

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: AppCreateInput; idempotencyKey: string }) => createApp(v.body, v.idempotencyKey),
    onSuccess: (app) => {
      qc.setQueryData<App[]>(qk.apps(app.projectId), (old) => (old ? [...old.filter((a) => a.id !== app.id), app] : [app]));
      void qc.invalidateQueries({ queryKey: qk.apps(app.projectId) });
    },
  });
}

/* ---------- Setup hub: edit and archive (Getting started, Apps & profiles) ---------- */

/** Rename, describe or archive a project (`PATCH /projects/{id}`, If-Match). An archived project leaves the lists. */
export function useProjectMutations() {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (v: { projectId: string; body: ProjectPatchInput; etag?: string }) => updateProject(v.projectId, v.body, v.etag),
    onSuccess: (project) => {
      qc.setQueryData<Project[]>(qk.projects, (old) => old?.flatMap((p) => (p.id !== project.id ? [p] : project.archivedAt ? [] : [project])));
      void qc.invalidateQueries({ queryKey: qk.projects });
      void qc.invalidateQueries({ queryKey: qk.me }); // `me.projects` lists live projects only
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 412) void qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
  return { update };
}

/**
 * Create and edit a project's environments (`POST /projects/{id}/environments`, `PATCH /environments/{id}` with
 * If-Match). Environments live inside the project document: every write moves the project's version, which is the
 * etag of the project and of all its environments, so the cached project takes the new environment and etag at once
 * (the next write sends the right If-Match) before the list refetches.
 */
export function useEnvironmentMutations(projectId: string) {
  const qc = useQueryClient();
  const put = (env: Environment) => {
    qc.setQueryData<Project[]>(qk.projects, (old) =>
      old?.map((p) =>
        p.id !== projectId
          ? p
          : { ...p, etag: env.etag ?? p.etag, environments: p.environments.some((e) => e.id === env.id) ? p.environments.map((e) => (e.id === env.id ? env : { ...e, etag: env.etag ?? e.etag })) : [...p.environments, env] },
      ),
    );
    void qc.invalidateQueries({ queryKey: qk.projects });
  };
  const refresh = (err: unknown) => {
    if (err instanceof ApiError && (err.status === 412 || err.status === 409)) void qc.invalidateQueries({ queryKey: qk.projects });
  };
  const create = useMutation({ mutationFn: (body: EnvironmentWriteInput) => createEnvironment(projectId, body), onSuccess: put, onError: refresh });
  const update = useMutation({ mutationFn: (v: { environmentId: string; body: EnvironmentPatchInput; etag?: string }) => updateEnvironment(v.environmentId, v.body, v.etag), onSuccess: put, onError: refresh });
  return { create, update };
}

/** Edit or archive an app (`PATCH /apps/{id}`, If-Match). The app's lockVersion is also its profiles' etag. */
export function useAppMutations() {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (v: { appId: string; body: AppPatchInput; etag?: string }) => updateApp(v.appId, v.body, v.etag),
    onSuccess: (app) => {
      qc.setQueryData<App[]>(qk.apps(app.projectId), (old) => old?.flatMap((a) => (a.id !== app.id ? [a] : app.archivedAt ? [] : [app])));
      void qc.invalidateQueries({ queryKey: qk.apps(app.projectId) });
      void qc.invalidateQueries({ queryKey: qk.profiles(app.id) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 412) void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
  return { update };
}

/** Apps of several projects (counts per project); the same cache entries as useApps. */
export const useAppsOfProjects = (projectIds: string[]) =>
  useQueries({ queries: projectIds.map((id) => ({ queryKey: qk.apps(id), queryFn: async () => (await listApps(id)).data.filter((a) => !a.archivedAt) })) });

/** Profiles of several apps (bundle ids, archive impact); the same cache entries as useProfiles. */
export const useProfilesOfApps = (appIds: string[], enabled = true) =>
  useQueries({ queries: appIds.map((id) => ({ queryKey: qk.profiles(id), queryFn: async () => (await listProfiles(id)).data.filter((p) => !p.archivedAt), enabled })) });

/** The effective env of one app per environment and platform (`POST /apps/{id}/env/resolve`, a read). */
export const useEnvResolutions = (appId: string | undefined, targets: Array<{ environment: string; platform: Platform }>, enabled = true) =>
  useQueries({
    queries: targets.map((t) => ({
      queryKey: qk.envResolve(appId ?? '', t.environment, t.platform),
      queryFn: () => resolveAppEnv(appId ?? '', { environment: t.environment, platform: t.platform }),
      enabled: Boolean(appId) && enabled,
      staleTime: 30_000,
    })),
  });
